import { addBreadcrumb as addSentryBreadcrumb } from '@sentry/react';
import { snakeCase } from 'lodash-es';
import { getLogger } from './instance';

const EXCEPTION_LOG_LOCAL_STORAGE_KEY = 'exception_logs';

/**
 * Logs unhandled API error responses to identify them so we can add the necessary error handling iteratively.
 *
 * Do not use for runtime exceptions where sending logs multiple times could happen due to multiple render cycles.
 * Instead, use `logExceptionOnce` for that.
 *
 * @param error - The error message or error object
 * @param data - Additional data to log. This should include `key` so we can easily trace the log back to a line of code
 *
 * @example
 * logUnknownErrorResponse('Error message', { key: 'universally.unique.period.separated.string', anyKey: value });
 */
export function logUnknownErrorResponse(
  error: unknown,
  data: Record<string, unknown> & { key: string }
) {
  const stringError = getStringError(error);

  if (process.env.PUBLIC_HR_ENV !== 'production') {
    // eslint-disable-next-line no-console -- allow console logging in non-prod envs for debugging
    console.error(stringError, data);
  }

  getLogger().log(
    'Unexpected error',
    {
      attention_required: true,
      error: stringError,
      data,
    },
    'error'
  );

  addSentryBreadcrumb({
    message: stringError,
    data,
    level: 'error',
  });
}

function getStringError(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Logs an exception once by checking if the error has already been logged in local storage.
 *
 * @param error - The error message or error object
 * @param logIdParams - Used for the local storage hash key. This should include `key` so we can easily trace the log back to a line of code. Do not include any large kv pairs in this object. KV pairs in this object are still logged to DD.
 * @param additionalData - Additional data to log to DD. Not used for the local storage log id. Useful for large messages or objects or unknown shapes.
 */
export function logExceptionOnce(
  error: unknown,
  logIdParams: Record<string, unknown> & { key: string },
  additionalData: Record<string, unknown> & { key?: never } = {}
) {
  let exceptionLogs: string[] = [];
  let exceptionLogsLocalStorage = localStorage.getItem(
    EXCEPTION_LOG_LOCAL_STORAGE_KEY
  );
  if (exceptionLogsLocalStorage) {
    try {
      exceptionLogs = JSON.parse(exceptionLogsLocalStorage);
    } catch {
      exceptionLogs = [];
    }
  }

  const stringError = getStringError(error);
  const logIdParamsStringified = JSON.stringify(logIdParams);
  const logId = `${snakeCase(stringError)}-${logIdParamsStringified}`;

  if (exceptionLogs.includes(logId)) {
    return;
  }

  exceptionLogs.push(logId);

  localStorage.setItem(
    EXCEPTION_LOG_LOCAL_STORAGE_KEY,
    JSON.stringify(exceptionLogs)
  );

  logUnknownErrorResponse(stringError, { ...logIdParams, ...additionalData });
}
